import React, { useCallback, useEffect, useRef, useState } from "react";
import { MicOff } from "lucide-react";
import { SpeechBubbleToast } from "@ever-tutor/et-ui";
import { useMicPermissionStore } from "@/features/tutor/store/useMicPermissionStore";
import { useModalStore, ModalType } from "@/store/useModalStore";

const TOAST_DURATION = 5;

interface MicSilenceToastProps {
  blobBottom?: number;
  onDeviceConfirmed?: (deviceId: string) => void;
}

const MicSilenceToast: React.FC<MicSilenceToastProps> = ({
  blobBottom,
  onDeviceConfirmed,
}) => {
  const visible = useMicPermissionStore((s) => s.silenceToastVisible);
  const setVisible = useMicPermissionStore((s) => s.setSilenceToastVisible);
  const openModal = useModalStore((s) => s.openModal);

  const [countdown, setCountdown] = useState(TOAST_DURATION);
  const [progressKey, setProgressKey] = useState(0);
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (visible) {
      setCountdown(TOAST_DURATION);
      setProgressKey((k) => k + 1);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setVisible(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [visible, setVisible]);

  const handleTest = useCallback(() => {
    pendingActionRef.current = () => {
      openModal(ModalType.MIC_SILENCE_TEST, { onDeviceConfirmed });
    };
    setVisible(false);
  }, [openModal, onDeviceConfirmed, setVisible]);

  const handleExitComplete = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  return (
    <SpeechBubbleToast
      open={visible}
      top={blobBottom}
      progressDurationS={TOAST_DURATION}
      progressKey={progressKey}
      onExitComplete={handleExitComplete}
      aria-label="Microphone test prompt"
    >
      <div
        style={{
          padding: "17px 17px 14px 17px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              flexShrink: 0,
              alignSelf: "center",
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "#E2FFE3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MicOff
              style={{ width: 22, height: 22, color: "#1C9C6D" }}
              aria-hidden
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#1a1a1a",
                lineHeight: 1.35,
                whiteSpace: "pre-line",
              }}
            >
              {"Seems like we couldn't\nhear you. Test your mic?"}
            </p>
            <p
              style={{
                margin: "5px 0 0",
                fontSize: 13,
                fontWeight: 500,
                color: "#999",
              }}
            >
              {`Closing in ${countdown}s`}
            </p>
          </div>
        </div>

        <button
          onClick={handleTest}
          style={{
            width: "100%",
            padding: "11px 0",
            background: "#1C9C6D",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 10,
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
            letterSpacing: 0.1,
          }}
        >
          Test mic
        </button>
      </div>
    </SpeechBubbleToast>
  );
};

export default MicSilenceToast;
